import { ReactElement, ReactNode } from 'react';
import { Button, Collapse, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui-compat';
import { useToggle } from 'react-use';
import { CSSClasses } from '@e2m-customer-portal/shared';
import { useMaxWidth, MaxWidthSteps } from '@e2m-customer-portal/hooks';
import { ExpandMoreIcon } from '../../assets/icons';

const useStyles = makeStyles<void, 'collapsed'>()((theme, _params, classes) => ({
  root: {
    margin: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(3, 0),
    },
  },
  backgroundDefault: {
    backgroundColor: theme.palette.background.default,
  },
  backgroundPaper: {
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    height: '48px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    transition: theme.transitions.create('background-color'),
  },
  title: {
    color: '#fff',
    fontSize: '.9rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: theme.spacing(0, 4),
    transition: theme.transitions.create('color'),
  },
  button: {
    borderRadius: 0,
    textTransform: 'none',
    justifyContent: 'space-between',
    padding: '0px',
    width: '100%',
    height: '100%',
  },
  icon: {
    fill: '#fff',
    marginRight: theme.spacing(2),
    transition: theme.transitions.create(['transform', 'fill']),
    transform: 'rotateX(180deg)',
    [`&.${classes.collapsed}`]: {
      transform: 'rotateX(0deg)',
    },
  },
  body: {},
  bodySpaced: {
    padding: theme.spacing(3),
  },
  collapsed: {},
}));

type ContainerVariant = 'default' | 'transparent' | 'paper';

export interface ContainerProps {
  /**
   * An optional id to apply to the container.
   */
  id?: string;

  /**
   * Optional class-name that will be applied to the root element of the container.
   */
  className?: string;

  /**
   * Optional class-names that will be applied to the respective elements.
   */
  classes?: CSSClasses<'root' | 'header' | 'title' | 'body' | 'collapsed'>;

  /**
   * Variant of the container.
   */
  variant?: ContainerVariant;

  /**
   * Title element. Will be displayed at the top of the container.
   */
  title?: ReactNode;

  /**
   * Body of the container.
   */
  children?: ReactNode;

  /**
   * Determine the max-width of the container. By default, the container width grows with the size of the screen.
   */
  maxWidth?: MaxWidthSteps;

  /**
   * If true, the container can be collapsed to hide the children. The container must have a title for this to work.
   */
  collapsible?: boolean;
}

export function Container({
  id,
  className,
  classes: classNames,
  variant = 'default',
  title,
  children,
  maxWidth,
  collapsible,
}: Readonly<ContainerProps>): ReactElement<ContainerProps> {
  const { classes, cx } = useStyles();
  const maxWidthClass = useMaxWidth(maxWidth);
  const [collapsed, toggleCollapsed] = useToggle(false);
  const collapsedClass = collapsed && classNames?.collapsed;

  return (
    <section
      data-testid={id}
      id={id}
      className={cx(
        classes.root,
        maxWidthClass,
        {
          [classes.backgroundDefault]: variant === 'default',
          [classes.backgroundPaper]: variant === 'paper',
        },
        classNames?.root,
        collapsedClass,
        className,
      )}
    >
      {title && (
        <header className={cx(classes.header, { [classes.collapsed]: collapsed }, collapsedClass, classNames?.header)}>
          {collapsible ? (
            <Button
              id={id}
              className={classes.button}
              endIcon={<ExpandMoreIcon className={cx(classes.icon, { [classes.collapsed]: collapsed })} />}
              onClick={toggleCollapsed}
            >
              <Typography
                variant="h5"
                className={cx(classes.title, { [classes.collapsed]: collapsed }, collapsedClass, classNames?.title)}
              >
                {title}
              </Typography>
            </Button>
          ) : (
            <Typography variant="h5" className={cx(classes.title, classNames?.title)}>
              {title}
            </Typography>
          )}
        </header>
      )}
      {collapsible ? (
        <Collapse in={!collapsed}>
          <div
            id={id}
            className={cx(
              classes.body,
              collapsedClass,
              { [classes.bodySpaced]: variant !== 'transparent' },
              classNames?.body,
            )}
          >
            {children}
          </div>
        </Collapse>
      ) : (
        <div
          id={id}
          className={cx(classes.body, { [classes.bodySpaced]: variant !== 'transparent' }, classNames?.body)}
        >
          {children}
        </div>
      )}
    </section>
  );
}
